import React from 'react';
import './WeeklyPlaybook.css';

// Weekly Playbook — quick-reference card for daily setup.
// Source: ultimate_sweep.py results, tuned over 5 months.
// Update SCHEDULE below if backtest parameters change.

const SCHEDULE = [
  {
    day: 'Mon',
    nifty: {
      window: '09:20 → 11:00', code: '+3', dte: 3, week: 'nearWk',
      margin: '~₹2.7L', pnl: '₹+86,163', win: '93%', star: true,
    },
    sensex: {
      window: '09:20 → 11:00', code: 'ATM', dte: 4, week: 'nearWk',
      margin: '~₹2.5L', pnl: '₹+47,040', win: '80%', star: false,
    },
    note: 'Both indexes 09:20→11:00 morning. NIFTY +3 OTM strangle = 93% win.',
  },
  {
    day: 'Tue',
    nifty: {
      window: '09:30 → 14:30', code: 'ATM', dte: 2, week: 'nearWk',
      margin: '~₹3.0L', pnl: '₹+1,61,032', win: '82%', star: true,
    },
    sensex: {
      window: '09:30 → 15:00', code: 'ATM', dte: 3, week: 'nearWk',
      margin: '~₹2.5L', pnl: '₹+92,910', win: '75%', star: false,
    },
    note: '🏆 BIGGEST WINNER. NIFTY DTE-2 ATM = ₹+1.6L over 17 trades. Exit 14:30, NOT 15:15.',
  },
  {
    day: 'Wed',
    nifty: {
      window: '10:30 → 14:30', code: '+2', dte: 1, week: 'nearWk',
      margin: '~₹2.7L', pnl: '₹+55,608', win: '76%', star: false,
    },
    sensex: {
      window: '09:45 → 15:00', code: 'ATM', dte: 2, week: 'nearWk',
      margin: '~₹2.5L', pnl: '₹+99,594', win: '85%', star: true,
    },
    note: '🔵 SENSEX DTE-2 ATM 09:45→15:00 = 85% win, worst day only ~₹-1K.',
  },
  {
    day: 'Thu',
    nifty: {
      window: '09:20 → 15:15', code: '+2', dte: 0, week: 'nearWk',
      margin: '~₹2.7L', pnl: '₹+48,386', win: '71%', star: false,
    },
    sensex: {
      window: '10:00 → 15:15', code: 'ATM', dte: 1, week: 'nearWk',
      margin: '~₹2.5L', pnl: '₹+86,121', win: '75%', star: true,
    },
    note: 'SENSEX 1-DTE full session. NIFTY 0-DTE OK only at +2 OTM (delta protection).',
  },
  {
    day: 'Fri',
    nifty: {
      window: '10:00 → 13:00', code: '+1', dte: 6, week: 'nearWk',
      margin: '~₹2.7L', pnl: '₹+49,927', win: '77%', star: true,
    },
    sensex: {
      window: '10:00 → 13:30', code: 'ATM', dte: 0, week: 'nearWk',
      margin: '~₹2.5L', pnl: '₹+23,831', win: '71%', star: false,
    },
    note: 'NIFTY next-week (6-DTE) +1 OTM. SENSEX 0-DTE — exit 13:30 sharp.',
  },
];

const DAY_INDEX = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5 };

function Cell({ data }) {
  const codeClass = data.code === 'ATM' ? 'code-atm' : 'code-otm';
  return (
    <div className={`pb-cell ${data.star ? 'pb-cell-star' : ''}`}>
      <div className="pb-cell-head">
        {data.star && <span className="pb-star" title="Recommended">⭐</span>}
        <span className="pb-window">{data.window}</span>
        <span className={`pb-code ${codeClass}`}>[{data.code}]</span>
      </div>
      <div className="pb-cell-meta">
        DTE {data.dte} · {data.week} · {data.margin}
      </div>
      <div className="pb-cell-stats">
        <span className="pb-pnl">{data.pnl}</span>
        <span className="pb-sep">·</span>
        <span className="pb-win">win {data.win}</span>
      </div>
    </div>
  );
}

export default function WeeklyPlaybook() {
  const todayIdx = new Date().getDay();

  return (
    <div className="page-playbook">
      <div className="pb-header">
        <h2>Weekly Playbook</h2>
        <span className="pb-sub">Tuned setups per weekday · Strangle = sell CE@ATM+offset AND PE@ATM−offset · 3 lots</span>
      </div>

      <div className="pb-table-wrap">
        <table className="pb-table">
          <thead>
            <tr>
              <th className="pb-col-day">DAY</th>
              <th>
                <span className="pb-index-badge pb-idx-nifty">NIFTY</span>
                <span className="pb-th-meta">LOT 75 · STEP 50 · WEEKLY TUE</span>
              </th>
              <th>
                <span className="pb-index-badge pb-idx-sensex">SENSEX</span>
                <span className="pb-th-meta">LOT 20 · STEP 100 · WEEKLY THU</span>
              </th>
              <th className="pb-col-notes">NOTES</th>
            </tr>
          </thead>
          <tbody>
            {SCHEDULE.map((row) => {
              const isToday = DAY_INDEX[row.day] === todayIdx;
              return (
                <tr key={row.day} className={isToday ? 'pb-row-today' : ''}>
                  <td className="pb-day-cell">
                    <div className="pb-day-name">{row.day}</div>
                    {isToday && <div className="pb-today-tag">TODAY</div>}
                  </td>
                  <td><Cell data={row.nifty} /></td>
                  <td><Cell data={row.sensex} /></td>
                  <td className="pb-note">{row.note}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pb-footer">
        <div className="pb-rule pb-rule-gold">
          <strong>⭐ GOLDEN RULE — never hold expiry-day into last 90 min:</strong>{' '}
          Tue NIFTY exit 14:30, Fri SENSEX exit 13:30. This rule keeps every month positive.
        </div>
        <div className="pb-rule">
          <strong>⭐ Phase-3a (₹4.4L margin):</strong>{' '}
          Trade only the green-bordered <span className="pb-inline-star">⭐</span> cell each day.
          5 trades/wk × +₹4.83L / 5 months = ~₹97K/mo. Worst single day in 5mo: ₹-10,833 (Tue Feb).
        </div>
        <div className="pb-rule">
          <strong>Phase-3b (~₹5.5L margin):</strong>{' '}
          Trade BOTH NIFTY + SENSEX cells daily. ₹+7.51L / 5mo ≈ ~₹1.50L/mo. Needs pledged collateral.
        </div>
        <div className="pb-rule">
          <strong>Setup — strike codes:</strong>{' '}
          [ATM]=at-the-money, [+1/+2/+3]=OTM offset (NIFTY ±50/100/150, SENSEX ±100/200/300).
          Strangle = sell CE@ATM+offset AND PE@ATM−offset (3 lots each). Always nearest weekly expiry, 3 lots.
        </div>
        <div className="pb-rule pb-rule-info">
          <strong>ⓘ Caveats:</strong>{' '}
          Tuned on 12–18 samples/bucket (5 months). Win rates will drift ±10%.
          Re-run <code>ultimate_sweep.py</code> after any exchange calendar shift.
        </div>
      </div>
    </div>
  );
}
